import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

import flagsmith from 'flagsmith'
import { IFlags } from 'flagsmith/types'

import { FEATURE_NAMES, FeatureName } from 'src/models/FeatureFlag'
import { TranscriptMetadata } from 'src/models/TranscriptMetadata'
import { debug } from 'src/services/DebugService'
import { getFlagsmithApiKey, getFlagsmithConfigOverrides, isMockActive } from 'src/utils/env'

import { useTranscriptMetadata } from './Transcript/MetadataContext'

export type FeatureFlagContextType = IFlags<FeatureName> | null

export type FlagsmithUserTraits = Pick<TranscriptMetadata, 'customerId' | 'customerType'>

export const applyDebugLogsFromFlags = (flags: { debug_logs?: { enabled?: boolean } } | null) => {
    debug.setState(Boolean(flags?.debug_logs?.enabled))
}

export const FeatureFlagContext = createContext<FeatureFlagContextType>(null)

type FeatureFlagProviderProps = {
    children: ReactNode
}

export const FeatureFlagProvider = (props: FeatureFlagProviderProps) => {
    const { children } = props
    const [flags, setFlags] = useState<FeatureFlagContextType>(null)
    const metadata = useTranscriptMetadata()
    const environmentID = getFlagsmithApiKey()

    useEffect(() => {
        if (!environmentID || !metadata?.customerId) {
            return
        }

        if (isMockActive()) {
            const featuresWithMocks = new Set(document.mocks.getEnabledFeatures())
            const mockedFlags = FEATURE_NAMES.reduce(
                (acc, curr) =>
                    Object.assign(acc, {
                        [curr]: {
                            enabled: featuresWithMocks.has(curr),
                        },
                    }),
                {} as NonNullable<FeatureFlagContextType>,
            )
            applyDebugLogsFromFlags(mockedFlags)
            setFlags(mockedFlags)

            return
        }

        const traits: FlagsmithUserTraits = {
            customerId: metadata.customerId,
            customerType: metadata.customerType,
        }
        flagsmith.init({
            environmentID,
            // Optionally source flags from a self-hosted Flagsmith instead of SaaS: unreachable
            // SaaS never resolves flags, and TranscriptProvider renders nothing while
            // useFeatureFlagsAvailable() is false, hanging the app on "Loading transcript…".
            ...getFlagsmithConfigOverrides(),
            cacheFlags: false,
            enableAnalytics: true, // See https://docs.flagsmith.com/flag-analytics/ for more info
            traits,
            identity: metadata.customerId.toString(),
            onChange: () => {
                const nextFlags = flagsmith.getAllFlags()
                applyDebugLogsFromFlags(nextFlags)
                setFlags(nextFlags)
            },
        })
        // listening only to changes in `customerId`, not `customerType`,
        // as the latter should not change without the former
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [environmentID, metadata?.customerId])

    return <FeatureFlagContext.Provider value={flags}>{children}</FeatureFlagContext.Provider>
}

export const useFeatureFlag = (featureName: FeatureName) => {
    const flags = useContext(FeatureFlagContext)

    return flags?.[featureName]?.enabled || false
}

export const useFeatureFlagsAvailable = () => {
    const flags = useContext(FeatureFlagContext)

    return !!flags
}
